import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingCart, Star, GitCompare, Truck, Image as ImageIcon, Check as CheckIcon, Camera } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatPrice, BuyerAPI, AdminAPI, CatalogAPI, resolveImage } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useAdminEdit } from '../context/AdminEditContext';
import { useToast } from '../hooks/use-toast';
import { useSuppliers } from '../hooks/useSuppliers';
import { useCartSyncBySupplier } from '../hooks/useCartSyncBySupplier';
import { useUserDiscount, applyDiscount } from '../hooks/useUserDiscount';

// BUG 5 fix: fallback image
const IMG_FALLBACK = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"><rect width="200" height="200" fill="%23F5F1EA"/><text x="100" y="110" text-anchor="middle" font-size="48" fill="%23C9BFAE">🦷</text></svg>';

const ProductCard = ({ product, onAddToCart, sponsored = false }) => {
  const { t } = useTranslation();
  const [selectedVariant, setSelectedVariant] = useState(0);
  const [favorited,       setFavorited]       = useState(false);
  const [inCompare,       setInCompare]       = useState(false);
  const { cartSupplierIds, cartSubtotalBySupplier } = useCartSyncBySupplier();
  const { user }    = useAuth();
  const { toast }   = useToast();
  const { editMode } = useAdminEdit();
  const [edit, setEdit] = useState({ name: product.name, brand: product.brand, category: product.category, image: product.image });
  const [savingEdit, setSavingEdit] = useState(false);
  const [cats, setCats] = useState([]);
  useEffect(() => { if (editMode && cats.length === 0) CatalogAPI.categories().then(setCats).catch(() => {}); }, [editMode]);
  const leafCats = cats.filter(c => c.level === 3 || (c.level === 2 && !cats.some(x => x.parent_id === c.id)));
  const saveProductField = async (patch) => {
    setSavingEdit(true);
    try { await AdminAPI.updateProduct(product.id, patch); toast({ title: 'Produit modifié' }); }
    catch (e) { toast({ title: 'Erreur', description: 'Échec' }); }
    setSavingEdit(false);
  };
  const uploadProductImg = async (file) => {
    if (!file) return;
    setSavingEdit(true);
    try {
      const up = await AdminAPI.upload(file);
      const url = up.url || (up.file_id ? `/api/files/${up.file_id}` : '');
      await AdminAPI.updateProduct(product.id, { image: url });
      setEdit((s) => ({ ...s, image: url }));
      toast({ title: 'Photo mise à jour' });
    } catch (e) { toast({ title: 'Erreur' }); }
    setSavingEdit(false);
  };
  const { byId: suppliersById } = useSuppliers();

  // Index de la première variante dont le fournisseur est déjà au panier (s'il y en a une)
  const matchingVariantIdx = React.useMemo(() => {
    if (!cartSupplierIds.size) return -1;
    return product.variants.findIndex(v => cartSupplierIds.has(v.supplier_id));
  }, [product.variants, cartSupplierIds]);

  // Auto-sélectionne la variante "même fournisseur" (par défaut on garde la plus économique sinon)
  useEffect(() => {
    if (matchingVariantIdx >= 0) setSelectedVariant(matchingVariantIdx);
  }, [matchingVariantIdx]);

  const variant     = product.variants[selectedVariant];
  const minPrice    = Math.min(...product.variants.map((v) => v.price));
  const { tier } = useUserDiscount();
  const priceInfo = applyDiscount(minPrice, tier);
  const hasCartMatch = matchingVariantIdx >= 0;
  const matchingVariant = hasCartMatch ? product.variants[matchingVariantIdx] : null;
  const matchSupplier = matchingVariant ? suppliersById[matchingVariant.supplier_id] : null;
  const matchSupplierName = matchSupplier?.name || '';
  const matchSubtotal = matchingVariant ? (cartSubtotalBySupplier[matchingVariant.supplier_id] || 0) : 0;
  const threshold = matchSupplier?.free_shipping_threshold ?? null;
  // Subtotal projeté si on ajoutait 1 unité de la variante "match"
  const projectedSubtotal = matchSubtotal + (matchingVariant?.price || 0);
  const hasFreeShipping = threshold !== null && threshold > 0;
  const freeShippingReached = hasFreeShipping && matchSubtotal >= threshold;
  const freeShippingUnlocksWithThis = hasFreeShipping && !freeShippingReached && projectedSubtotal >= threshold;
  const remainingToFree = hasFreeShipping && projectedSubtotal < threshold
    ? (threshold - projectedSubtotal)
    : 0;

  // BUG 9 fix: sync favorited depuis localStorage (évite appel API à chaque card)
  useEffect(() => {
    try {
      const cached = JSON.parse(sessionStorage.getItem('dz_favs') || '[]');
      setFavorited(cached.includes(product.id));
    } catch {}
  }, [product.id]);

  // Sync comparateur depuis localStorage
  useEffect(() => {
    try {
      const list = JSON.parse(localStorage.getItem('dz_compare') || '[]');
      setInCompare(list.includes(product.id));
    } catch {}
    const refresh = () => {
      try { setInCompare(JSON.parse(localStorage.getItem('dz_compare') || '[]').includes(product.id)); } catch {}
    };
    window.addEventListener('compare-changed', refresh);
    return () => window.removeEventListener('compare-changed', refresh);
  }, [product.id]);

  const toggleFav = async (e) => {
    e.preventDefault();
    if (!user) { window.location.href = '/connexion'; return; }
    try {
      if (favorited) {
        await BuyerAPI.removeFavorite(product.id);
        // BUG 9 fix: mettre à jour le cache sessionStorage
        try {
          const cached = JSON.parse(sessionStorage.getItem('dz_favs') || '[]');
          sessionStorage.setItem('dz_favs', JSON.stringify(cached.filter(id => id !== product.id)));
        } catch {}
      } else {
        await BuyerAPI.addFavorite(product.id);
        try {
          const cached = JSON.parse(sessionStorage.getItem('dz_favs') || '[]');
          sessionStorage.setItem('dz_favs', JSON.stringify([...cached, product.id]));
        } catch {}
      }
      setFavorited(f => !f);
    } catch {}
  };

  const toggleCompare = (e) => {
    e.preventDefault();
    try {
      let list = JSON.parse(localStorage.getItem('dz_compare') || '[]');
      if (list.includes(product.id)) {
        list = list.filter(id => id !== product.id);
        setInCompare(false);
        toast({ title: 'Retiré du comparateur' });
      } else {
        if (list.length >= 4) { toast({ title: 'Maximum 4 produits à comparer' }); return; }
        list.push(product.id);
        setInCompare(true);
        toast({ title: 'Ajouté au comparateur', description: `${list.length}/4` });
      }
      localStorage.setItem('dz_compare', JSON.stringify(list));
      window.dispatchEvent(new Event('compare-changed'));
    } catch {}
  };

  if (editMode) {
    return (
      <div className="bg-white border-2 border-dashed border-[#1F4D3F]/40 rounded-xl overflow-hidden flex flex-col" data-testid={`product-card-edit-${product.id}`}>
        <div className="relative aspect-square bg-[#FAF7F2] overflow-hidden flex items-center justify-center">
          {edit.image ? <img src={resolveImage(edit.image)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-10 h-10 text-[#D8CFC0]" />}
          <label className="absolute bottom-2 left-2 bg-white/90 text-[#1F4D3F] text-[10px] px-2 py-1 rounded cursor-pointer hover:bg-white">
            {savingEdit ? '...' : <><Camera className="w-3 h-3 inline mr-1" />Photo</>}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadProductImg(e.target.files[0])} />
          </label>
        </div>
        <div className="p-3 flex flex-col gap-2">
          <div>
            <label className="text-[10px] text-[#7A6F5C] uppercase">Marque</label>
            <input defaultValue={edit.brand} onBlur={(e) => e.target.value !== product.brand && saveProductField({ brand: e.target.value })}
              className="w-full text-xs border border-[#E8E0D0] rounded px-2 py-1" placeholder="Marque" />
          </div>
          <div>
            <label className="text-[10px] text-[#7A6F5C] uppercase">Titre</label>
            <textarea defaultValue={edit.name} onBlur={(e) => e.target.value !== product.name && saveProductField({ name: e.target.value })}
              className="w-full text-sm border border-[#E8E0D0] rounded px-2 py-1 resize-none" rows={2} placeholder="Nom du produit" />
          </div>
          <div>
            <label className="text-[10px] text-[#7A6F5C] uppercase">Catégorie</label>
            <select defaultValue={edit.category} onChange={(e) => saveProductField({ category: e.target.value })}
              className="w-full text-xs border border-[#E8E0D0] rounded px-2 py-1 bg-white">
              <option value={edit.category}>{(cats.find(c => c.id === edit.category) || {}).name || edit.category}</option>
              {leafCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="text-[10px] text-[#7A6F5C] flex items-center gap-1">
            {savingEdit ? 'Enregistrement…' : <><CheckIcon className="w-3 h-3 text-emerald-600" /> Modifications auto-sauvegardées</>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`card-hover group bg-white border rounded-xl overflow-hidden flex flex-col transition-colors ${hasCartMatch ? 'border-[#1F4D3F] ring-1 ring-[#1F4D3F]/30' : 'border-[#E8E0D0]'}`} data-testid={`product-card-${product.id}`}>
      <div className="relative aspect-square bg-[#FAF7F2] overflow-hidden">
        <Link to={`/produit/${product.id}`} className="block w-full h-full">
          {/* BUG 5 fix: onError fallback */}
          <img
            src={product.image || IMG_FALLBACK}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={e => { e.target.src = IMG_FALLBACK; }}
          />
        </Link>
        {product.discount && (
          <div className="pointer-events-none absolute top-3 left-3 bg-[#C75B27] text-white text-xs font-semibold px-2 py-1 rounded">
            -{product.discount}%
          </div>
        )}
        {hasCartMatch && (
          <div
            className="pointer-events-none absolute bottom-3 left-3 max-w-[calc(100%-1.5rem)] bg-[#1F4D3F] text-white text-[10px] uppercase tracking-wider px-2 py-1 rounded shadow flex items-center gap-1"
            data-testid={`same-supplier-badge-${product.id}`}
            title={matchSupplierName ? `Variante du fournisseur "${matchSupplierName}" déjà présent dans votre panier` : "Même fournisseur que votre panier"}
          >
            <ShoppingCart className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">
              {matchSupplierName ? `Aussi chez ${matchSupplierName}` : 'Même fournisseur'}
            </span>
          </div>
        )}
        {sponsored && (
          <div className="pointer-events-none absolute top-3 right-10 bg-white/95 text-[#7A6F5C] text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-[#E8E0D0]">
            Sponsorisé
          </div>
        )}
        {/* Favori */}
        <button onClick={toggleFav}
          className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-white border border-[#E8E0D0] flex items-center justify-center hover:bg-[#FAF7F2] shadow-sm"
          aria-label="Ajouter aux favoris">
          <Heart className={`w-4 h-4 ${favorited ? 'fill-[#C75B27] text-[#C75B27]' : 'text-[#7A6F5C]'}`} />
        </button>
        {/* Comparateur */}
        <button onClick={toggleCompare}
          className={`absolute top-3 right-3 w-8 h-8 rounded-full border flex items-center justify-center shadow-sm transition-colors
            ${inCompare ? 'bg-[#1F4D3F] border-[#1F4D3F] text-white' : 'bg-white border-[#E8E0D0] text-[#7A6F5C] hover:border-[#1F4D3F] hover:text-[#1F4D3F]'}`}
          title={inCompare ? 'Dans le comparateur' : 'Ajouter au comparateur'}>
          <GitCompare className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <div className="text-xs text-[#7A6F5C] uppercase tracking-wider">{product.brand}</div>
        <Link to={`/produit/${product.id}`}
          className="mt-1 text-sm font-medium text-[#2D2A26] hover:text-[#1F4D3F] line-clamp-2 min-h-[40px]">
          {product.name}
        </Link>

        <div className="flex items-center gap-1 mt-2 text-xs text-[#7A6F5C]">
          <Star className="w-3.5 h-3.5 fill-[#E0A52E] text-[#E0A52E]" />
          <span className="font-medium text-[#2D2A26]">{product.rating || 0}</span>
          <span>({product.reviews || 0})</span>
          {product.variants.length > 1 && (
            <span className="ml-auto text-[#1F4D3F] font-medium">{product.variants.length} offres</span>
          )}
        </div>

        <div className="mt-3 space-y-1">
          {product.variants.slice(0, 3).map((v, idx) => {
            const isMatch = cartSupplierIds.has(v.supplier_id);
            const key = `${v.supplier_id || 'unknown'}-${v.label || idx}`;
            return (
            <button key={key} onClick={() => setSelectedVariant(idx)}
              className={`w-full flex items-center justify-between text-xs px-2 py-1.5 rounded border
                ${idx === selectedVariant
                  ? 'border-[#1F4D3F] bg-[#F5F1EA] text-[#1F4D3F]'
                  : isMatch
                    ? 'border-[#1F4D3F]/40 bg-[#F5F1EA]/50 text-[#1F4D3F]'
                    : 'border-transparent hover:bg-[#FAF7F2] text-[#5A5247]'}`}>
              <span className="truncate flex items-center gap-1.5">
                {isMatch && <span className="w-1.5 h-1.5 rounded-full bg-[#1F4D3F] flex-shrink-0" title={t('cart.same_supplier')} />}
                {v.label}
              </span>
              <span className="font-medium whitespace-nowrap ml-2">{t('product.from')} {formatPrice(v.price)}</span>
            </button>
            );
          })}
        </div>

        {hasCartMatch && (freeShippingReached || freeShippingUnlocksWithThis || remainingToFree > 0) && (
          <div
            className={`mt-3 px-2 py-1.5 rounded text-[11px] flex items-center gap-1.5 border
              ${freeShippingReached || freeShippingUnlocksWithThis
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : 'bg-amber-50 border-amber-200 text-amber-900'}`}
            data-testid={`free-shipping-hint-${product.id}`}
          >
            <Truck className="w-3.5 h-3.5 flex-shrink-0" />
            {freeShippingReached ? (
              <span><strong>Livraison gratuite atteinte</strong> chez {matchSupplierName}</span>
            ) : freeShippingUnlocksWithThis ? (
              <span><strong>Cet ajout débloque la livraison gratuite</strong> chez {matchSupplierName}</span>
            ) : (
              <span>
                Encore <strong>{formatPrice(remainingToFree)}</strong> chez {matchSupplierName} pour la livraison gratuite
              </span>
            )}
          </div>
        )}

        <div className="mt-3 pt-3 border-t border-[#F5F1EA] flex items-end justify-between">
          <div>
            <div className="text-[10px] text-[#7A6F5C] uppercase tracking-wider">{t('product.from')}</div>
            {priceInfo.hasDiscount ? (
              <div>
                <div className="flex items-baseline gap-2">
                  <div className="font-serif text-xl font-semibold text-[#1F4D3F]" data-testid="product-price-discounted">{formatPrice(priceInfo.discounted)}</div>
                  <div className="text-xs text-[#7A6F5C] line-through">{formatPrice(priceInfo.original)}</div>
                </div>
                <div className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded bg-[#FFF8EC] border border-[#F0E0B8] text-[10px] text-[#7A6520]" data-testid="product-price-tier-badge">
                  {tier.label} −{priceInfo.percent}%
                </div>
              </div>
            ) : (
              <div className="font-serif text-xl font-semibold text-[#1F4D3F]">{formatPrice(minPrice)}</div>
            )}
          </div>
          <button
            onClick={() => onAddToCart && onAddToCart(product, variant)}
            className="w-10 h-10 rounded-full bg-[#1F4D3F] hover:bg-[#173B30] text-white flex items-center justify-center"
            aria-label={t('product.add_to_cart')}>
            <ShoppingCart className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
